// src/utils/dynamicUrlMapping.js
/**
 * Dynamic URL mapping system that handles backend categories automatically
 * This system creates URL slugs dynamically from category names and caches them
 */

/**
 * Convert a category/subcategory name to a URL-safe slug
 * @param {string} name - Category or subcategory name
 * @returns {string} URL-safe slug
 */
export const createUrlSlug = (name) => {
  if (!name || typeof name !== 'string') return '';
  
  return name
    .toLowerCase()
    .trim()
    // Replace common symbols and characters
    .replace(/&/g, 'and')
    .replace(/[''`]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    // Replace spaces and multiple hyphens with single hyphen
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-|-$/g, '');
};

/**
 * Convert a URL slug back to a display name (best effort)
 * @param {string} slug - URL slug
 * @param {Array} availableCategories - Array of available category names from backend
 * @returns {string} Display name or null if not found
 */
export const slugToDisplayName = (slug, availableCategories = []) => {
  if (!slug) return null;
  
  // First try exact match from available categories
  for (const category of availableCategories) {
    if (createUrlSlug(category) === slug) {
      return category;
    }
  }
  
  // Fallback: Convert slug to title case as best effort
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/\bAnd\b/g, '&'); // Convert 'and' back to '&'
};

/**
 * Cache for URL mappings to avoid repeated calculations
 */
class UrlMappingCache {
  constructor() {
    this.categoryToSlug = new Map();
    this.slugToCategory = new Map();
    this.subcategoryToSlug = new Map();
    this.slugToSubcategory = new Map();
    this.lastUpdated = null;
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Check if cache needs refresh
   */
  needsRefresh() {
    if (!this.lastUpdated) return true;
    return Date.now() - this.lastUpdated > this.cacheExpiry;
  }

  /**
   * Update cache with fresh data from backend
   * @param {Array} categories - Array of category objects from backend
   * @param {Array} subcategories - Array of subcategory objects from backend
   */
  updateCache(categories = [], subcategories = []) {
    // Clear existing cache
    this.categoryToSlug.clear();
    this.slugToCategory.clear();
    this.subcategoryToSlug.clear();
    this.slugToSubcategory.clear();

    // Process categories
    categories.forEach(category => {
      const name = typeof category === 'string' ? category : category.name;
      if (name) {
        const slug = createUrlSlug(name);
        this.categoryToSlug.set(name, slug);
        this.slugToCategory.set(slug, name);
      }
    });

    // Process subcategories
    subcategories.forEach(subcategory => {
      const name = typeof subcategory === 'string' ? subcategory : subcategory.name;
      if (name) {
        const slug = createUrlSlug(name);
        this.subcategoryToSlug.set(name, slug);
        this.slugToSubcategory.set(slug, name);
      }
    });

    this.lastUpdated = Date.now();
    
    // Store in localStorage for persistence
    try {
      localStorage.setItem('urlMappingCache', JSON.stringify({
        categoryToSlug: Array.from(this.categoryToSlug.entries()),
        slugToCategory: Array.from(this.slugToCategory.entries()),
        subcategoryToSlug: Array.from(this.subcategoryToSlug.entries()),
        slugToSubcategory: Array.from(this.slugToSubcategory.entries()),
        lastUpdated: this.lastUpdated
      }));
    } catch (error) {
      console.warn('Failed to cache URL mappings to localStorage:', error);
    }
  }

  /**
   * Load cache from localStorage
   */
  loadFromStorage() {
    try {
      const cached = localStorage.getItem('urlMappingCache');
      if (cached) {
        const data = JSON.parse(cached);
        
        // Check if cache is still valid
        if (data.lastUpdated && (Date.now() - data.lastUpdated < this.cacheExpiry)) {
          this.categoryToSlug = new Map(data.categoryToSlug);
          this.slugToCategory = new Map(data.slugToCategory);
          this.subcategoryToSlug = new Map(data.subcategoryToSlug);
          this.slugToSubcategory = new Map(data.slugToSubcategory);
          this.lastUpdated = data.lastUpdated;
          return true;
        }
      }
    } catch (error) {
      console.warn('Failed to load URL mapping cache from localStorage:', error);
    }
    return false;
  }

  /**
   * Get category slug
   */
  getCategorySlug(categoryName) {
    return this.categoryToSlug.get(categoryName) || createUrlSlug(categoryName);
  }

  /**
   * Get category name from slug
   */
  getCategoryName(slug) {
    return this.slugToCategory.get(slug);
  }

  /**
   * Get subcategory slug
   */
  getSubcategorySlug(subcategoryName) {
    return this.subcategoryToSlug.get(subcategoryName) || createUrlSlug(subcategoryName);
  }

  /**
   * Get subcategory name from slug
   */
  getSubcategoryName(slug) {
    return this.slugToSubcategory.get(slug);
  }

  /**
   * Get all available categories
   */
  getAllCategories() {
    return Array.from(this.categoryToSlug.keys());
  }

  /**
   * Get all available subcategories
   */
  getAllSubcategories() {
    return Array.from(this.subcategoryToSlug.keys());
  }
}

// Global cache instance
export const urlMappingCache = new UrlMappingCache();

/**
 * Initialize URL mapping cache
 * Should be called on app startup
 */
export const initializeUrlMapping = async () => {
  // Try to load from localStorage first
  const loadedFromCache = urlMappingCache.loadFromStorage();
  
  if (!loadedFromCache || urlMappingCache.needsRefresh()) {
    // Fetch fresh data from backend using actual OriginSelect API endpoints
    try {
      // Get default country list for initial mapping
      const defaultCountries = 'canada,usa';
      
      const [categoriesResponse, subcategoriesResponse] = await Promise.all([
        // Use actual OriginSelect categories API
        fetch(`/api/products/categories?countries=${defaultCountries}`)
          .then(res => res.json())
          .then(data => data.categories || [])
          .catch(() => []),
        
        // Note: Subcategories are loaded per-category, so we'll collect them dynamically
        // For initial setup, start with empty subcategories
        Promise.resolve([])
      ]);

      urlMappingCache.updateCache(categoriesResponse, subcategoriesResponse);
    } catch (error) {
      console.error('Failed to fetch categories for URL mapping:', error);
      
      // Fallback to static mappings if API fails
      const fallbackCategories = [
        'Baby & Kids',
        'Beauty',
        'Beauty & Personal Care',
        'Home Decor',
        'Grocery & Gourmet Foods',
        'Health & Wellness',
        'Clothing',
        'Beverages',
        'Books & Media'
      ];
      
      urlMappingCache.updateCache(fallbackCategories, []);
    }
  }
};

/**
 * Refresh URL mapping cache
 * Call this when you know new categories have been added
 */
export const refreshUrlMapping = async () => {
  urlMappingCache.lastUpdated = null; // Force refresh
  await initializeUrlMapping();
};

/**
 * Get category URL slug with fallback
 * @param {string} categoryName - Category name
 * @returns {string} URL slug
 */
export const getCategorySlug = (categoryName) => {
  if (!categoryName) return '';
  return urlMappingCache.getCategorySlug(categoryName);
};

/**
 * Get category name from URL slug
 * @param {string} slug - URL slug
 * @returns {string|null} Category name or null if not found
 */
export const getCategoryFromSlug = (slug) => {
  if (!slug) return null;
  
  const cachedName = urlMappingCache.getCategoryName(slug);
  if (cachedName) return cachedName;
  
  // Fallback: Check if any available category matches this slug
  const availableCategories = urlMappingCache.getAllCategories();
  return slugToDisplayName(slug, availableCategories);
};

/**
 * Get subcategory URL slug with fallback
 * @param {string} subcategoryName - Subcategory name
 * @returns {string} URL slug
 */
export const getSubcategorySlug = (subcategoryName) => {
  if (!subcategoryName) return '';
  return urlMappingCache.getSubcategorySlug(subcategoryName);
};

/**
 * Get subcategory name from URL slug
 * @param {string} slug - URL slug
 * @returns {string|null} Subcategory name or null if not found
 */
export const getSubcategoryFromSlug = (slug) => {
  if (!slug) return null;
  
  const cachedName = urlMappingCache.getSubcategoryName(slug);
  if (cachedName) return cachedName;
  
  // Fallback: Check if any available subcategory matches this slug
  const availableSubcategories = urlMappingCache.getAllSubcategories();
  return slugToDisplayName(slug, availableSubcategories);
};

/**
 * Validate if a category exists
 * @param {string} categoryName - Category name to validate
 * @returns {boolean} True if category exists
 */
export const isValidCategory = (categoryName) => {
  const availableCategories = urlMappingCache.getAllCategories();
  return availableCategories.includes(categoryName);
};

/**
 * Validate if a subcategory exists
 * @param {string} subcategoryName - Subcategory name to validate
 * @returns {boolean} True if subcategory exists
 */
export const isValidSubcategory = (subcategoryName) => {
  const availableSubcategories = urlMappingCache.getAllSubcategories();
  return availableSubcategories.includes(subcategoryName);
};

/**
 * Hook to refresh URL mappings when categories change
 * Use this in admin interfaces or when you add new categories
 */
export const useUrlMappingRefresh = () => {
  return {
    refreshMappings: refreshUrlMapping,
    isValidCategory,
    isValidSubcategory,
    getAllCategories: () => urlMappingCache.getAllCategories(),
    getAllSubcategories: () => urlMappingCache.getAllSubcategories()
  };
};
